import React, { useState, useEffect } from 'react';
import { Mail, User, AlertCircle, Brain } from 'lucide-react';
import api from '../services/api';

const PRITAM_OPENING = `(Pritam is sitting in his room. He looks up briefly as you enter, makes eye contact for a moment, then glances down at his hands with a slight smile) Hello`;

const LoginPage = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendHealthy, setBackendHealthy] = useState(null);
  const [isCheckingBackend, setIsCheckingBackend] = useState(true);

  // Check backend health on mount with retry logic
  useEffect(() => {
    checkBackendHealthWithRetry();
  }, []);

  const checkBackendHealthWithRetry = async (maxRetries = 3) => {
    setIsCheckingBackend(true);
    let attempts = 0;
    
    while (attempts < maxRetries) {
      try {
        const healthy = await api.checkHealth();
        setBackendHealthy(healthy);
        setIsCheckingBackend(false);
        return; // Success - exit
      } catch (err) {
        attempts++;
        console.log(`Backend health check attempt ${attempts}/${maxRetries} failed:`, err.message);
        
        if (attempts < maxRetries) {
          // Wait longer on each retry (exponential backoff)
          const waitTime = attempts * 3000; // 3s, 6s, 9s
          console.log(`Retrying in ${waitTime/1000} seconds...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        } else {
          // All retries failed
          console.error('Backend health check failed after all retries');
          setBackendHealthy(false);
          setIsCheckingBackend(false);
        }
      }
    }
  };

  const validateEmail = (email) => {
    return email.includes('@') && email.includes('.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!email.trim() || !name.trim()) {
      setError('Please enter both email and name');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email');
      return;
    }

    setIsLoading(true);

    try {
      const userId = email.trim().toLowerCase();
      
      // Smart Resume Logic (matching Python implementation)
      const sessions = await api.getUserSessions(userId);

      let sessionData = {
        userId,
        userName: name.trim(),
        sessionId: null,
        sessionName: null,
        messages: [{
          role: 'ai',
          content: PRITAM_OPENING,
          timestamp: new Date().toISOString()
        }]
      };

      // If user has existing sessions
      if (sessions && sessions.length > 0) {
        const latestSession = sessions[0];

        // If latest session has messages, load it
        if (latestSession.message_count > 0) {
          try {
            const conversation = await api.getConversation(userId, latestSession.session_id);
            sessionData = {
              userId,
              userName: name.trim(),
              sessionId: latestSession.session_id,
              sessionName: conversation.session_name,
              messages: conversation.messages
            };
          } catch (err) {
            console.error('Error loading conversation:', err);
            // Use default greeting on error
          }
        }
      }

      // Call parent component's login handler
      onLoginSuccess(sessionData);

    } catch (err) {
      setError(`Failed to login: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        
        {/* Header */}
        <div className="login-header">
          <div className="icon-circle">
            <Brain className="brain-icon" />
          </div>
          <h1 className="main-title">AI Therapy Client Simulator</h1>
          <p className="subtitle">Practice your therapy skills with Pritam, an AI client</p>
        </div>

        {/* Backend Health Check - Loading State */}
        {isCheckingBackend && (
          <div className="alert alert-warning">
            <AlertCircle className="alert-icon" />
            <div>
              <p className="alert-title">Waking up Pritam...</p>
              <p className="alert-text">This may take 10-15 seconds on first load</p>
            </div>
          </div>
        )}

        {/* Backend Health Check - Error State */}
        {backendHealthy === false && !isCheckingBackend && (
          <div className="alert alert-error">
            <AlertCircle className="alert-icon" />
            <div>
              <p className="alert-title">Backend server is not responding</p>
              <p className="alert-text">Please try refreshing the page. If the problem persists, the server may be down.</p>
              <code className="alert-code">python backend_gemini.py</code>
            </div>
          </div>
        )}

        {/* Login Form */}
        <div className="card">
          <h2 className="card-title">Welcome! Please enter your details to begin:</h2>

          <form onSubmit={handleSubmit} className="login-form">
            
            {/* Email Input */}
            <div className="form-group">
              <label htmlFor="email">Your Email</label>
              <div className="input-wrapper">
                <Mail className="input-icon" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g., john@gmail.com"
                  disabled={isLoading || backendHealthy === false}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Name Input */}
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <div className="input-wrapper">
                <User className="input-icon" />
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., John Smith"
                  disabled={isLoading || backendHealthy === false}
                  autoComplete="name"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="alert alert-warning">
                <AlertCircle className="alert-icon" />
                <p className="alert-text">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || backendHealthy === false || isCheckingBackend}
              className="btn btn-primary btn-full"
            >
              {isLoading ? (
                <>
                  <div className="spinner" />
                  Loading sessions...
                </>
              ) : (
                'Start Practicing'
              )}
            </button>
          </form>
        </div>

        {/* Footer - Updated Message */}
        <p className="login-footer">
          {isCheckingBackend 
            ? "Pritam can take a few seconds to load" 
            : "Please be patient :)"
          }
        </p>
      </div>
    </div>
  );
};

export default LoginPage;